import React, { useEffect, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";

/// Long enough to register as a spin rather than a flicker.
const MIN_SPIN_MS = 450;

/// A count whose own footprint is the way back from a stale list: on hover it
/// becomes a refresh button, so a header that fetches once — the org roster, a
/// Shared-with-me page whose spaces never poll — gains an affordance without
/// gaining a control. Font size is inherited so the count sits at whatever
/// scale its header uses.
export const RefreshableCount: React.FC<{
  /// The count that gives up its footprint on hover. Omit it for a header with
  /// no count — a section of meters rather than a list — and the icon sits in
  /// flow, visible without a hover, sharing the spinner and the cooldown.
  label?: string;
  /// Names what gets refreshed, for the label and the tooltip ("members",
  /// "shared items").
  subject: string;
  onRefresh: () => Promise<void> | void;
  /// Minimum gap between refreshes — a stale list is a nuisance, not an
  /// emergency, and the button sits under the pointer for a whole read.
  cooldownMs?: number;
}> = ({ label, subject, onRefresh, cooldownMs = 5000 }) => {
  const [spinning, setSpinning] = useState(false);
  const [cooling, setCooling] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const refresh = async () => {
    if (spinning || cooling) return;
    setSpinning(true);
    try {
      // Floored, because a counter fetch against a warm projection resolves in
      // under a frame: the spinner would mount and unmount without ever being
      // painted, and a button that answers a press with nothing reads as dead.
      await Promise.all([
        onRefresh(),
        new Promise((resolve) => setTimeout(resolve, MIN_SPIN_MS)),
      ]);
    } finally {
      setSpinning(false);
      setCooling(true);
      timer.current = setTimeout(() => setCooling(false), cooldownMs);
    }
  };

  const busy = spinning || cooling;

  const icon = (
    <RefreshIcon
      sx={{ fontSize: 12 }}
      className={spinning ? "animate-spin" : ""}
    />
  );

  return (
    <button
      type="button"
      onClick={() => void refresh()}
      disabled={busy}
      aria-label={`Refresh ${subject}`}
      title={cooling ? "Just refreshed" : `Refresh ${subject}`}
      className="group/count relative inline-flex items-center justify-center font-mono text-fg-muted hover:text-fg disabled:hover:text-fg-muted"
      style={{ fontSize: "inherit", fontVariantNumeric: "tabular-nums" }}
    >
      {label === undefined ? (
        icon
      ) : (
        <>
          {/* The label keeps the width; the icon takes its place, so nothing in
              the header shifts when the pointer arrives. */}
          <span
            className={spinning ? "invisible" : "group-hover/count:invisible"}
          >
            {label}
          </span>
          <span
            className={`absolute inset-0 flex items-center justify-center ${
              spinning ? "" : "opacity-0 group-hover/count:opacity-100"
            }`}
          >
            {icon}
          </span>
        </>
      )}
    </button>
  );
};
